/**
 * O seletor que tem uma opção só.
 *
 * Empresa e clínica são recorte antes de serem filtro: nas telas de agenda nada carrega antes
 * de a clínica ser escolhida, e no contexto de uma clínica a lista tem sempre um item — a
 * própria. O mesmo acontece com a empresa na clínica que atende uma. O que a pessoa encontrava
 * ao entrar era uma tela vazia atrás de um campo com uma linha, a ser respondido para a tela
 * começar.
 *
 * Aqui a escolha se faz sozinha e o campo não aparece. Com duas ou mais, nada muda.
 *
 * Enquanto a lista não tem resposta o campo também não aparece — e isso não é detalhe: lista
 * vazia por ainda não ter chegado é indistinguível de lista vazia de verdade, e decidir ali
 * desenhava o campo por um instante para escondê-lo em seguida, o pisca que a tela dava ao
 * abrir.
 */

import { useEffect } from 'react';

export interface EscolhaUnica {
  /** Falso enquanto não há resposta, e falso quando a resposta é uma opção só. */
  mostrarSeletor: boolean;
}

export interface OpcoesDaEscolhaUnica {
  /**
   * A lista já tem resposta — do servidor, da falha ou da recusa. Sem isto, quem não sabe
   * distinguir "ainda não chegou" de "não há" fica com o padrão de lista pronta.
   */
  resolvida?: boolean;
}

export function useEscolhaUnica<T>(
  opcoes: readonly T[],
  escolhida: T | null,
  escolher: (opcao: T) => void,
  { resolvida = true }: OpcoesDaEscolhaUnica = {}
): EscolhaUnica {
  const unica = resolvida && opcoes.length === 1 ? opcoes[0] : undefined;

  useEffect(() => {
    if (unica && !escolhida) {
      escolher(unica);
    }
    // `escolher` costuma ser função nova a cada render de quem chama; incluí-la na lista
    // reexecutaria o efeito sem parar. O que decide é a opção única e o que já está escolhido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unica, escolhida]);

  return { mostrarSeletor: resolvida && unica === undefined };
}

export default useEscolhaUnica;
